<template>
  <div
    class="user-profile-container"
    style="max-height: 100%; overflow-y: auto; padding: 0px 10px; overflow-x: hidden"
  >
    <el-form label-position="top" ref="userDetails2" :model="formModel">
      <template v-if="updated">
        <div v-for="field in orderedFields" :key="field.key">
          <el-form-item
            :label="field.label || field.key"
            :prop="field.formProp"
            :rules="field.isCustom ? field.rules : undefined"
          >
            <!-- Standard Fields -->
            <el-input
              v-if="!field.isCustom && field.component === 'el-input'"
              v-model="formModel[field.key]"
              :type="field.props.type"
              :rows="field.props.rows"
              :maxlength="field.props.maxlength"
              :show-word-limit="field.props['show-word-limit']"
            ></el-input>
            <!-- Custom Fields -->
            <component
              v-else-if="field.isCustom"
              v-model="formModel.customValues"
              :name="field.key"
              :setting="field.setting"
              :is="field.component"
              :mode="smartFieldMode"
              @blur="validateField(field.key)"
            ></component>
          </el-form-item>
        </div>
      </template>
      <el-form-item>
        <el-button type="primary" @click="saveUserDetails">Save</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import SmartFieldString from "@/components/ModuleEditor/SmartFields/String";
import SmartFieldSelect from "@/components/ModuleEditor/SmartFields/Select";
import SmartFieldMultiselect from "@/components/ModuleEditor/SmartFields/Multiselect";
import SmartFieldTextarea from "@/components/ModuleEditor/SmartFields/Textarea";
import _ from "lodash";

const STANDARD_FIELD_DEFINITIONS = Object.freeze([
  {
    key: "name",
    label: "Name",
    component: "el-input",
    props: { type: "text" },
  },
  {
    key: "alias",
    label: "Alias",
    component: "el-input",
    props: { type: "text" },
  },
  {
    key: "mobileNo",
    label: "Mobile No",
    component: "el-input",
    props: { type: "text" },
  },
  {
    key: "email",
    label: "Email",
    component: "el-input",
    props: { type: "text" },
  },
  {
    key: "comments",
    label: "Comments",
    component: "el-input",
    props: { type: "textarea", rows: 6, maxlength: 2048, "show-word-limit": true },
  },
]);

export default {
  name: "UserDetails",
  props: ["selectedChat", "value"],
  components: {
    SmartFieldString,
    SmartFieldSelect,
    SmartFieldMultiselect,
    SmartFieldTextarea,
  },
  data() {
    return {
      customValues: {},
      updated: false,
      smartFieldMode: "form",
      formModel: {
        ...(this.value || {}),
        customValues: {},
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.updated = false;
        const customUserDetailsFields = _.get(
          this,
          "$store.state.modules.handover.customUserProfile.fields",
          {}
        );
        const extended = Object.keys(customUserDetailsFields).reduce((acc, curr) => {
          return !newVal[curr]
            ? { ...acc, [curr]: customUserDetailsFields[curr]?.default || "" }
            : { ...acc, [curr]: newVal[curr] };
        }, {});
        this.customValues = extended;
        this.formModel = {
          ...newVal,
          customValues: extended,
        };
        this.$nextTick(() => {
          this.updated = true;
        });
      },
    },
  },
  computed: {
    customFields() {
      const customUserDetailsEnabled = _.get(
        this,
        "$store.state.modules.handover.customUserProfile.enabled",
        false
      );
      if (!customUserDetailsEnabled) {
        return {};
      }
      const customUserDetailsFields = _.get(
        this,
        "$store.state.modules.handover.customUserProfile.fields",
        {}
      );
      return customUserDetailsFields;
    },
    orderedFields() {
      const customOrders = _.get(
        this.$store.state.modules.handover,
        "customUserProfile.orders",
        null
      );

      let orders;
      if (customOrders && customOrders.length > 0) {
        orders = customOrders;
      } else {
        const defaultStandardFields = ["name", "alias", "mobileNo", "email", "comments"];
        const customFieldKeys = _.keys(this.customFields);
        orders = [...defaultStandardFields, ...customFieldKeys];
      }

      return orders
        .map((key) => {
          const standardFieldDef = STANDARD_FIELD_DEFINITIONS.find((f) => f.key === key);
          if (standardFieldDef) {
            return {
              ...standardFieldDef,
              isCustom: false,
              formProp: key,
            };
          }

          const customFieldDef = this.customFields[key];
          if (customFieldDef) {
            return {
              key: key,
              label: customFieldDef.label || key,
              rules: customFieldDef.rules,
              setting: customFieldDef,
              component: this.toComponent(customFieldDef),
              isCustom: true,
              formProp: `customValues.${key}`,
            };
          }
          return null;
        })
        .filter((field) => field !== null);
    },
  },
  methods: {
    toComponent(setting) {
      const type = _.capitalize(setting.type);
      const componentName = `SmartField${type}`;
      const componentExist = this.$options.components[componentName];
      return componentExist ? componentName : "SmartFieldString";
    },
    validateField(field) {
      this.$refs.userDetails2.validateField(`customValues.${field}`);
    },
    saveUserDetails() {
      this.$refs.userDetails2.validate((valid) => {
        if (!valid) {
          this.$message({
            type: "warning",
            message: "Please fix the validation errors before saving",
          });
          return;
        }
        const userDetails = {
          ...this.formModel,
          ...this.formModel.customValues,
        };
        delete userDetails.customValues;

        this.$store
          .dispatch("UPDATE_USER_DETAILS", {
            chat: this.selectedChat,
            userDetails,
          })
          .then((isSuccessful) => {
            if (isSuccessful) {
              this.$message({
                type: "success",
                message: "Successfully saved user details",
              });
            } else {
              this.$message({
                type: "error",
                message: "Failed to save user details",
              });
            }
          })
          .catch((err) => {
            console.error("Error saving user details:", err);
            this.$message({
              type: "error",
              message: "An unexpected error occurred while saving user details",
            });
          });
      });
    },
  },
};
</script>
<style scoped>
.user-profile-container .el-form-item {
  margin-bottom: 8px;
}
</style>